<template>
    <v-dialog v-model="dialogOpen" max-width="700px">
        <v-card color="grey darken-3">
            <toolbar-page title="Select locales" disable-icon>
                <template slot="buttons">

                    <v-combobox :items="locales"
                                v-model="sourceLocale"
                                item-text="code"
                                solo
                                hide-details
                                dense
                                background-color="grey darken-3"
                                style="max-width: 130px"
                                return-object>
                    </v-combobox>

                    <v-tooltip top color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on" @click="dialogOpen=false">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </template>
                        <span>Close</span>
                    </v-tooltip>
                </template>
            </toolbar-page>

            <v-data-table
                :headers="tableHeaders"
                :items="locales"
                item-key="id"
                hide-default-footer
                must-sort
                class="elevation-0"
                sort-by="country"
                @input="emit"
            >
                <template v-slot:body="{ items }">
                    <tbody>
                    <tr v-for="locale in localesWithModels" :key="locale.id">

                        <td>
                            <div class="d-flex">
                                <div class="align-self-center">
                                    <v-checkbox v-model="locale.model"
                                                class="ma-0"
                                                @change="checked(locale, $event)"
                                                hide-details
                                                :disabled="minLocalesSelected && locale.model === true"
                                    ></v-checkbox>
                                </div>
                            </div>
                        </td>

                        <td>
                            {{ locale.country }}
                        </td>

                        <td>
                            {{ locale.language }}
                        </td>

                        <td>
                            {{ locale.language_tag }}
                        </td>

                        <td v-if="translate">
                            <v-switch v-model="locale.translate"></v-switch>
                        </td>
                    </tr>
                    </tbody>
                </template>

            </v-data-table>

            <v-btn color="primary"
                   class="ma-3"
                   :disabled="buttonDisabled"
                   @click="doneClicked">
                Done
            </v-btn>

        </v-card>
    </v-dialog>
</template>

<script>

import Locale from './Locale';
import ToolbarPage from './ToolbarPage';

export default {

    components: {
        Locale,
        ToolbarPage
    },

    props: {
        value: {
            type: Boolean,
            default: false,
            required: false
        },

        locales: {
            type: Array | null,
            required: true,
        },

        selected: {
            type: Array,
            required: true
        },

        translate: {
            type: Boolean,
            required: false,
            default: false,
        },

        showSource: {
            type: Boolean,
            required: false,
            default: false,
        }
    },

    watch: {
        selected(newSelected) {
            this.localesSelected = [...newSelected];
        }
    },

    data() {
        return {
            localesSelected: [],
            localesPrevious: null,
            sourceLocale: null,
            headers: [
                {
                    text: 'Select',
                    sortable: false,
                    value: 'country'
                },
                {
                    text: 'Country',
                    sortable: true,
                    value: 'country'
                },
                {
                    text: 'Language',
                    sortable: true,
                    value: 'language'
                },
                {
                    text: 'Code',
                    align: 'start',
                    sortable: true,
                    value: 'language_tag',
                },
            ],
        }
    },

    computed: {

        localesWithModels() {
            if (this.locales) {
                return this.locales.map(locale => {
                    locale.model = !!this.localesSelected.find(l => l.id === locale.id);
                    return locale;
                });
            }
        },

        tableHeaders() {

            if (!this.translate) return this.headers;

            return [...this.headers, ...[
                {
                    text: 'AI Translate',
                    sortable: false,
                    value: 'enabled'
                }
            ]];
        },


        dialogOpen: {
            get() {
                return this.value;
            },
            set() {
                this.closeDialog();
            }
        },

        buttonDisabled() {
            return this.localesSelected.length === 0;
        },

        minLocalesSelected() {
            return this.localesSelected.length === 1;
        },
    },


    created() {
        if (this.selected) {
            if (!this.localesPrevious) this.localesPrevious = [...this.selected];
            this.localesSelected = [...this.selected];
        }

        this.sourceLocale = Object.assign({}, this.locales[0]);
    },

    methods: {

        checked(locale, event) {

            if (event) {
                this.localesSelected.push(locale);
            } else {
                const index = this.localesSelected.findIndex(l => l.id === locale.id);
                this.localesSelected.splice(index, 1);
            }
            this.emit();
        },

        doneClicked() {
            this.emit();
            this.closeDialog();
        },

        emit() {
            this.emitIncrementalChanges();
            this.$emit('change', this.localesSelected);
        },

        emitIncrementalChanges() {
            const previousIds = this.localesPrevious.map(locale => locale.id);
            const currentIds = this.localesSelected.map(locale => locale.id);

            const removed = previousIds.filter(id => !currentIds.includes(id));
            const added = currentIds.filter(id => !previousIds.includes(id));

            if (removed) {
                removed.forEach(id => {
                    this.$emit('disable', this.locales.find(locale => locale.id === id));
                })
            }

            if (added) {
                added.forEach(id => {
                    if (this.showSource) {
                        this.$emit('enable', {
                            locale: this.locales.find(locale => locale.id === id),
                            source: this.sourceLocale
                        });
                    } else {
                        this.$emit('enable', this.locales.find(locale => locale.id === id));
                    }
                })
            }

            this.localesPrevious = [...this.localesSelected];
        },

        closeDialog() {
            this.$emit('input', false);
        }
    }
}
</script>
